import { Button } from '@crowdex/ui/components/button';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { useEffect, useRef, useState } from 'react';
import { useDesktopUpdateStatus } from '@/hooks/use-desktop-update-status';
import {
  checkForDesktopUpdate,
  downloadDesktopUpdate,
  installDesktopUpdate,
} from '@/lib/electron-bridge';

/**
 * The update banner at the top of the main window: a new version, its download, or why it
 * failed. "Restart to update" downloads it and restarts once it is ready; "Later" hides that
 * version only, so the next one shows again.
 */
export function DesktopUpdateBanner() {
  const status = useDesktopUpdateStatus();
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Asked to restart while the update was still downloading: install it once it is ready.
  const installWhenReady = useRef(false);

  useEffect(() => {
    // A download that failed ends the request: a later one, from Settings, restarts nothing.
    if (status?.state === 'error') installWhenReady.current = false;
    if (status?.state !== 'ready' || !installWhenReady.current) return;
    installWhenReady.current = false;
    installDesktopUpdate().catch((error: unknown) =>
      setActionError(error instanceof Error ? error.message : String(error)),
    );
  }, [status]);

  if (status === null) return null;
  const errorMessage = actionError ?? (status.state === 'error' ? status.message : null);

  async function run(action: () => Promise<unknown>) {
    setActionError(null);
    try {
      await action();
    } catch (error) {
      installWhenReady.current = false;
      setActionError(error instanceof Error ? error.message : String(error));
    }
  }

  if (errorMessage !== null) {
    if (errorMessage === dismissedError) return null;
    return (
      <UpdateNotice
        actions={
          <>
            <Button onClick={() => setDismissedError(errorMessage)} size="xs" variant="ghost">
              Dismiss
            </Button>
            <Button onClick={() => void run(checkForDesktopUpdate)} size="xs" variant="outline">
              Try again
            </Button>
          </>
        }
        title="Crowdex could not update."
        tone="error"
      >
        <span className="line-clamp-1 break-all" title={errorMessage}>
          {errorMessage}
        </span>
      </UpdateNotice>
    );
  }

  if (status.state === 'downloading')
    return (
      <UpdateNotice
        actions={
          <div className="flex items-center gap-2 text-muted-foreground text-xs tabular-nums">
            <div className="h-1 w-24 overflow-hidden rounded-full bg-control">
              <div
                className="h-full rounded-full bg-primary transition-[width]"
                style={{ width: `${Math.round(status.percent)}%` }}
              />
            </div>
            {Math.round(status.percent)}%
          </div>
        }
        title={`Downloading Crowdex ${status.version}…`}
        tone="info"
      />
    );

  if (status.state !== 'available' && status.state !== 'ready') return null;
  if (status.version === dismissedVersion) return null;
  const ready = status.state === 'ready';
  return (
    <UpdateNotice
      actions={
        <>
          <Button
            onClick={() => {
              installWhenReady.current = false;
              setDismissedVersion(status.version);
            }}
            size="xs"
            variant="ghost"
          >
            Later
          </Button>
          <Button
            onClick={() => {
              if (ready) return void run(installDesktopUpdate);
              installWhenReady.current = true;
              return void run(downloadDesktopUpdate);
            }}
            size="xs"
          >
            Restart to update
          </Button>
        </>
      }
      title={`Crowdex ${status.version} is available.`}
      tone="info"
    >
      You are on {status.currentVersion}.
    </UpdateNotice>
  );
}

/**
 * Clear of the title bar's window controls and leading inset, as the conversation header is;
 * clickable over a maximized side panel, where the conversation floats without pointer events.
 */
function UpdateNotice(props: Parameters<typeof InlineMessage>[0]) {
  return (
    <div className="pointer-events-auto shrink-0 pt-2 pr-[max(0.75rem,var(--window-controls-inset,0px))] pl-[max(0.75rem,var(--shell-leading-inset,0px))]">
      <InlineMessage compact {...props} />
    </div>
  );
}
